/**
 * Route paths the rate-limit config (`../rate-limits.ts`) keys on, each declared once for every
 * site that must agree on it: the route's mount, `AUTH_BYPASS_PATHS`, the rate-limit key and
 * any URL an email embeds.
 *
 * They live apart from their contracts because the rate-limit config is on `@wayai/core`'s root,
 * which every backend Worker isolate loads through `@wayai/runtime`: imported from their
 * contracts, those contracts' schemas would load in every isolate too (gh #5083, decision 61;
 * `scripts/check-backend-startup-graph.ts`). The contracts barrel exports them. Keep this module
 * free of imports.
 */

// ---------------------------------------------------------------------------
// The `/api/data` proxy (`./rekor-attestation`)
// ---------------------------------------------------------------------------

/**
 * WayAI-side mount for the attested `/v1` proxy, relative to the `/api` router. `data` —
 * not `rekor` — because the path is integrator-facing and the code name stays off
 * those surfaces; it also matches the pinned `data.wayai.pro` hostname.
 */
export const DATA_PROXY_MOUNT = '/data';

/**
 * The absolute path the proxy answers on. `app.ts` mounts from `DATA_PROXY_MOUNT`, the
 * rate-limit config keys off this, and the handler strips this prefix — so a rename moves
 * all three together. Without that, renaming the mount would silently 404 the whole proxy
 * (the prefix strip returns null on a mismatch) with nothing failing to catch it.
 */
export const DATA_PROXY_PREFIX = `/api${DATA_PROXY_MOUNT}`;

// ---------------------------------------------------------------------------
// Production base destruction (`./base-destruction`)
// ---------------------------------------------------------------------------

/**
 * WayAI-side mount for the destruction control plane, relative to the `/api` router.
 *
 * Deliberately NOT under `DATA_PROXY_MOUNT`: everything below that prefix is forwarded
 * verbatim to the data Worker's `/v1` tree by any caller holding `org:write`, which is
 * exactly the authority this flow exists to sit above. A separate mount keeps initiate,
 * confirm and cancel on routes WayAI validates itself.
 */
export const BASE_DESTRUCTION_MOUNT = '/bases';

/** Absolute prefix the destruction routes answer on. Rate-limit config keys off this. */
export const BASE_DESTRUCTION_PREFIX = `/api${BASE_DESTRUCTION_MOUNT}`;

/**
 * The emailed link's path. Public (mounted before `authMiddleware`, in `AUTH_BYPASS_PATHS`)
 * because it is opened from a cold mail client with no session.
 *
 * **This route CONFIRMS NOTHING.** It verifies the token's signature and hands the reader to
 * the console; the transition happens on `baseDestructionConfirmPath` (`./base-destruction`), a
 * POST. The split is not ceremony — a GET that mutates is completed by anything that
 * dereferences a URL, and enterprise mail-security scanners, link rewriters and gateway
 * prefetchers all do exactly that. Confirming on GET would have armed a production erasure seconds after the mail landed,
 * with no human involved, which is the one thing this whole flow exists to prevent. The
 * `GET /api/users/deletion/undo` precedent is safe on GET only because it STOPS a deletion;
 * dereferencing this one would start one.
 *
 * Declared here rather than spelled at each site: the mount, the bypass entry, the rate-limit
 * key and the URL the email embeds must move together, and a drift between any two of them
 * either 401s every confirmation link or leaves an unauthenticated path un-rate-limited.
 */
export const BASE_DESTRUCTION_CONFIRM_PATH = `${BASE_DESTRUCTION_PREFIX}/destruction/confirm`;

// ---------------------------------------------------------------------------
// Email unsubscribes (`./communities`, `./contact-book`) and automation webhooks (`./automations`)
// ---------------------------------------------------------------------------

/** The unsubscribe route's path: a public path (`AUTH_BYPASS_PATHS`), the address an email's one-click header names. */
export const COMMUNITY_EMAIL_UNSUBSCRIBE_PATH = '/api/communities/email-unsubscribe';

/** The one-click unsubscribe route's path: a public path (`AUTH_BYPASS_PATHS`), the address an email's one-click header names. */
export const CONTACT_BOOK_EMAIL_UNSUBSCRIBE_PATH = '/api/contact-book/email-unsubscribe';

/** Where webhook deliveries are received, outside `/api` (no bearer token; the secret is the credential). */
export const AUTOMATION_WEBHOOK_MOUNT = '/webhooks/automations';

// ---------------------------------------------------------------------------
// Managed Agents webhooks (`./managed-agents-webhook`)
// ---------------------------------------------------------------------------

/** Where a hosted agent connection's Managed Agents session events arrive, outside `/api`. */
export const MANAGED_AGENTS_WEBHOOK_MOUNT = '/webhooks/managed-agents';
